/**
 * Roads, paths, bridges and cosmetic props — the buildable DECORATION set.
 *
 * Single source of truth on the client for the two questions decoration answers
 * differently from a house: does the hero walk THROUGH it, and does it draw
 * UNDER him. Both collision (views/farm/scene.ts buildCollision) and the
 * renderer (views/farm/plots.ts) read these lists, so a kind can never be
 * walkable in one file and solid in the other — that mismatch is an invisible
 * wall in the middle of a road, the worst bug this feature can ship.
 *
 * Mirrors WALKABLE_DECOR / SOLID_DECOR in backend field.config.ts (the server
 * owns the costs and the placement cap; walkability is a client-side movement
 * concern, since the server has no map).
 */

/** Ground you walk ON: it is drawn flat, beneath the hero and beneath buildings. */
export const GROUND_DECOR: ReadonlySet<string> = new Set([
  'road', 'road_sand', 'path', 'path_pebbles',
  'bridge_wood_h', 'bridge_wood_v', 'bridge_stone_h', 'bridge_stone_v',
]);

/** Props that stand on their tile: solid, y-sorted with the hero like a building. */
export const PROP_DECOR: ReadonlySet<string> = new Set([
  'well', 'fountain', 'barrel', 'flower_barrel', 'signpost',
  'bench', 'bench_stone', 'hay_bale', 'scarecrow',
]);

export const isGroundKind = (kind: string): boolean => GROUND_DECOR.has(kind);
export const isDecorKind = (kind: string): boolean => GROUND_DECOR.has(kind) || PROP_DECOR.has(kind);

/**
 * Can the hero stand on this placed building? Only ground decoration — torches
 * and lanterns are handled separately in buildCollision (thin posts placed in
 * numbers), everything else is a wall.
 */
export const isWalkableBuildingKind = (kind: string): boolean => isGroundKind(kind);

/**
 * Kinds the server bills to the DECORATION budget even though they are not decor.
 *
 * Owner's rule: the crafting stations and the lights are not "buildings" — you
 * own one of each station by design, and torches are placed in numbers to keep
 * mobs off the farm, so neither should eat the 20-building allowance. The tent is
 * absent because it is never a Building row at all, and fences have their own
 * bucket.
 *
 * Deliberately NOT added to PROP_DECOR: that set also decides collision and draw
 * order, and a torch is already handled there as a thin post. This is a budget
 * question only. Mirrors UNCAPPED_KINDS in backend field.service.ts — the server
 * is the authority; a mismatch here only ever shows the wrong "no room" message.
 */
export const DECOR_BUDGET_EXTRA: ReadonlySet<string> = new Set([
  'workbench', 'furnace', 'forge',
  'torch', 'lantern',
]);

/** Does this kind draw from the decoration budget rather than the building cap? */
export const isDecorBudgetKind = (kind: string): boolean =>
  isDecorKind(kind) || DECOR_BUDGET_EXTRA.has(kind);
